import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@folio/components';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import {
  claimsListQueryOptions,
  getClaimsCategoryLabel,
  groupClaimsByCategory,
} from '@folio/utils/claims';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PolicyClaimsCategory } from './PolicyClaimsCategory';
import { Center, Stack } from '@folio/components/ui/flex';

type PolicyClaimsModuleProps = {
  title?: string;
  claimsMetadata?: Record<string, string>;
  /**
   * 'add-only' allows adding new claims even when the policy is no longer in
   * new-business state (e.g. during an MTA). Pre-existing claims remain
   * read-only; only claims created within the current session are editable.
   */
  mode?: 'new-business' | 'add-only';
  fromDate?: Date;
};

export function PolicyClaimsModule({
  title,
  claimsMetadata,
  mode = 'new-business',
  fromDate,
}: PolicyClaimsModuleProps) {
  const { t } = useTranslation();
  const { policy } = usePolicyOnDashboard();
  const claimsConfig = usePolicyProductConfig('claims');

  const {
    data: currentClaims,
    status: currentClaimsQueryStatus,
    refetch: refetchClaims,
  } = useQuery({
    ...claimsListQueryOptions(policy.id, claimsMetadata),
    select: data => data.data,
    placeholderData: keepPreviousData,
  });

  return (
    <Card>
      <CardHeader className="flex flex-row justify-between items-start space-y-0">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Stack gap={1}>
          {currentClaimsQueryStatus === 'pending' && (
            <Stack gap={2}>
              <Skeleton className="h-[40px] w-full" />
              <Skeleton className="h-[40px] w-full" />
              <Skeleton className="h-[40px] w-full" />
              <Skeleton className="h-[40px] w-full" />
              <Skeleton className="h-[40px] w-full" />
              <Skeleton className="h-[40px] w-full" />
            </Stack>
          )}
          {currentClaimsQueryStatus === 'error' && (
            <Center className="h-full">{t('policyClaims.fetchError')}</Center>
          )}
          {currentClaimsQueryStatus === 'success' &&
            groupClaimsByCategory(currentClaims, claimsConfig.claimsCategories).map(group => (
              <PolicyClaimsCategory
                key={group.category}
                category={group.category}
                label={getClaimsCategoryLabel(policy.productId, group, t)}
                hasResponsibility={group.hasResponsibility}
                claimsMetadata={claimsMetadata}
                categoryClaims={group.claims}
                fromDate={fromDate}
                refetchClaims={refetchClaims}
                mode={mode}
              />
            ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
